import { webApiService } from '../../services/web-api';
import { sentry } from '../../services/sentry/sentry';
import { setLSValue } from '../../utils/locale-storage';
import { USER_DATA } from '../../constants/common';
import { ipInfoService } from '../../services/ip';
import userDataApiService from '../../services/web-api/user-data-api';
import useQueryParams from '../../hooks/use-query-params';
import { checkIsComplianceMode } from '../app-mode/helpers';
import analytics from '../../services/analytics';

const useUserTrack = () => {
  const { updateQueryParams } = useQueryParams();

  const trackCurrentUser = async (customerId: string, adsData: Record<string, string>) => {
    try {
      const currentUser = await webApiService.getCustomerById(String(customerId));

      analytics.setUserId(customerId);
      sentry.setUser({ id: customerId });
      setLSValue(USER_DATA, {
        user_id: customerId,
        ads_data: adsData,
        ...(currentUser?.contactEmail ? { email: currentUser?.contactEmail } : {}),
      });
    } catch (error: unknown) {
      console.error('Error tracking current user:', error);
      throw error;
    }
  };

  const trackNewUser = async (adsData: Record<string, string>) => {
    try {
      const newUser = await webApiService.createCustomer({
        metadata: {
          install: 'web',
        },
        ...(checkIsComplianceMode()
          ? {
              testValues: {
                intro_email_template: 'welcome_pwa_white',
              },
            }
          : {}),
      });

      analytics.setUserId(newUser.customerId);
      sentry.setUser({ id: newUser.customerId });
      setLSValue(USER_DATA, { user_id: newUser.customerId, ads_data: adsData });

      const userIp = await ipInfoService.getInfo();
      const userAgent = window.navigator.userAgent;

      const collectedData = {
        event_name: 'page_load',
        web_app_id: process.env.REACT_APP_NAME_APP || '',
        event_source_url: window.location.href,
        client_user_agent: userAgent,
        customer_id: newUser.customerId,
        ip: userIp.ip || '',
        country: userIp.country || '',
        zip: userIp.postal || '',
        city: userIp.city || '',
        ads_network_data: [adsData],
      };

      await userDataApiService.loadCustomerTracking(collectedData);
      await webApiService.updateCustomerMetadata(newUser.customerId, { adsData });
      updateQueryParams({ customer_id: newUser.customerId });

      return newUser;
    } catch (error: unknown) {
      console.error('Error tracking new user:', error);
      throw error;
    }
  };

  return { trackNewUser, trackCurrentUser };
};

export { useUserTrack };
